import type { useNotificationStore } from '@/core/notifications/useNotificationStore';
import { trackException } from '@/services/applicationInsights';
import { trackRaygunError } from '@/services/raygun';
import { useDateFormat } from '@/composables/useDateFormat';

export interface ErrorLog {
  message: string;
  stack?: string;
  timestamp: Date;
  url: string;
  userAgent: string;
  context?: string;
}

// Type for the notification store interface
type NotificationStore = ReturnType<typeof useNotificationStore>;

class ErrorService {
  private isInitialized = false;
  private notificationStore: NotificationStore | null = null;

  //   Initialize global error handlers
  //   Sets up:
  //   1. window.onerror - Catches uncaught JavaScript errors (syntax errors, runtime errors like
  //      undefined variables, type errors, reference errors, etc.)
  //   2. window.onunhandledrejection - Catches unhandled promise rejections (async/await errors,
  //      rejected promises without .catch(), failed fetch requests without error handling)
  //   Must be called once at app startup (in main.ts) before any other code runs.
  //   Prevents duplicate initialization via isInitialized flag.
  //   Note: Vue component errors are handled separately via app.config.errorHandler in main.ts

  init() {
    if (this.isInitialized) return;
    this.isInitialized = true;

    this.setupWindowErrorHandler();
    this.setupUnhandledRejectionHandler();
  }

  // Inject notification store (must be called after Pinia is initialized)
  setNotificationStore(store: NotificationStore) {
    this.notificationStore = store;
  }

  // Setup window error handler
  private setupWindowErrorHandler() {
    globalThis.window.addEventListener('error', (event) => {
      this.handleError(event.error || new Error(String(event.message)));
      event.preventDefault(); // Prevent default browser error handling
    });
  }

  // Setup unhandled promise rejection handler
  private setupUnhandledRejectionHandler() {
    globalThis.window.addEventListener('unhandledrejection', (event) => {
      this.handleError(event.reason);
      event.preventDefault(); // Prevent default browser error handling
    });
  }

  handleError(error: Error | unknown, context?: string) {
    const errorLog = this.createErrorLog(error, context);

    // Log to console in development
    if (import.meta.env.DEV) {
      console.error('[ErrorService]', errorLog);
    }

    this.logToRemote(errorLog);

    this.showUserNotification(error);
  }

  private createErrorLog(error: Error | unknown, context?: string): ErrorLog {
    const err = error instanceof Error ? error : new Error(String(error));
    const { actions: dateActions } = useDateFormat();

    return {
      message: err.message,
      stack: err.stack,
      timestamp: dateActions.utcNowDate(),
      url: globalThis.location.href,
      userAgent: navigator.userAgent,
      ...(context && { context }),
    };
  }

  private async logToRemote(errorLog: ErrorLog) {
    try {
      const error = new Error(errorLog.message);
      if (errorLog.stack) {
        error.stack = errorLog.stack;
      }

      trackException(error, {
        context: errorLog.context || 'unknown',
        url: errorLog.url,
        userAgent: errorLog.userAgent,
        timestamp: errorLog.timestamp.toISOString(),
      });

      trackRaygunError(error, {
        context: errorLog.context || 'unknown',
        url: errorLog.url,
        userAgent: errorLog.userAgent,
        timestamp: errorLog.timestamp.toISOString(),
      });
    } catch (logError) {
      console.error('[ErrorService] Failed to log to remote:', logError);
    }
  }

  // Show user-friendly notification
  private showUserNotification(error: Error | unknown) {
    if (!this.notificationStore) {
      console.warn('[ErrorService] Notification store not initialized, skipping user notification');
      return;
    }

    const message = this.getUserFriendlyMessage(error);
    this.notificationStore.error(message);
  }

  // Convert technical error to user-friendly message
  private getUserFriendlyMessage(error: Error | unknown): string {
    if (error instanceof Error) {
      const message = error.message.toLowerCase();

      // If the error message doesn't contain "exception", it's likely a user-friendly
      // business logic error from the API (Result.Failure) and should be shown directly
      if (!message.includes('exception')) {
        return error.message;
      }

      // Network errors
      if (error.message.includes('fetch') || error.message.includes('network')) {
        return 'Unable to connect to the server. Please check your internet connection.';
      }

      // Auth errors
      if (error.message.includes('401') || error.message.includes('unauthorized')) {
        return 'Your session has expired. Please log in again.';
      }

      // Validation errors
      if (error.message.includes('validation')) {
        return 'Please check your input and try again.';
      }
    }

    return 'An unexpected error occurred. Please try again or contact support if the problem persists.';
  }
}

export const errorService = new ErrorService();
